<template>
  <div class="tournament-player-tile-container">
    <!-- Header with pairing statistics -->
    <div class="tile-container-header">
      <span> {{ pairingStats.paired }}/{{ pairingStats.total }} paired</span>
      <span v-if="pairingStats.unpaired > 0" class="header-info">
        &nbsp;({{ pairingStats.unpaired }} unpaired)
      </span>
    </div>

    <div
      class="tile-container-content"
      :class="{ 'drop-zone-active': isDragOver }"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div class="tiles-wrapper">
        <TournamentPlayerTile
          v-for="standing in standings"
          :key="standing.player.id"
          :standing="standing"
          :fullPlayer="standing.fullPlayer"
          :selectedUsername="selectedUsername"
          :isAbsent="isAbsent(standing.player.id)"
          :isPlayed="isPlayed(standing.player.id)"
          :isPaired="isPaired(standing.player.id)"
          :colorPref="standing.colorPref"
          :isDraggable="true"
          :totalRounds="totalRounds"
          :roundNumber="round.roundNumber"
          @player-selected="handlePlayerSelected"
        />

        <!-- Empty state message when all players are paired -->
        <div v-if="standings.length === 0" class="mt-6">
          <p class="has-text-grey">All players are paired to games</p>
          <p class="has-text-grey-light is-size-7">
            Drag players here to remove them from games
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TournamentPlayerTile from "./TournamentPlayerTile.vue";
import {
  isPlayerInRound,
  getColorPref,
} from "vue-chess-shared/tournament/utils/playerUtils";
import { TOURNAMENT_GAME_RESULT } from "vue-chess-shared/constants";
import { buildCompletePlayerStandings } from "@/utils/tournament-player-utils";

export default {
  name: "TournamentPlayerTileContainer",

  components: {
    TournamentPlayerTile,
  },

  props: {
    // Same props as TournamentRoundPairingStats for compatibility
    tournament: {
      type: Object,
      required: true,
    },

    players: {
      type: Array,
      required: true,
    },

    games: {
      type: Array,
      required: true,
    },

    round: {
      type: Object,
      required: true,
    },

    roundGames: {
      type: Array,
      required: true,
    },

    // Additional props for tile functionality
    isDraggable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedUsername: null,
      isDragOver: false,
    };
  },

  computed: {
    totalRounds() {
      if (!this.tournament) return null;
      return this.tournament.totalRounds;
    },

    standings() {
      return buildCompletePlayerStandings(
        this.round,
        this.players,
        this.isPaired,
        getColorPref
      );
    },

    /**
     * Calculate pairing statistics for the header
     */
    pairingStats() {
      const totalPlayers = this.players.length;
      const pairedPlayers = this.players.filter((player) =>
        this.isPaired(player.username)
      ).length;
      const unpairedPlayers = totalPlayers - pairedPlayers;

      return {
        paired: pairedPlayers,
        total: totalPlayers,
        unpaired: unpairedPlayers,
      };
    },
  },

  methods: {
    handlePlayerSelected(username) {
      if (this.selectedUsername === username) {
        this.selectedUsername = null;
      } else {
        this.selectedUsername = username;
      }
    },

    isCurrent(username) {
      return this.selectedUsername === username;
    },

    isTarget(username) {
      return this.selectedUsername && this.selectedUsername !== username;
    },

    isPlayed(target) {
      if (!this.selectedUsername) return false;

      const highlightUserPlayedGames = this.games.filter((g) => {
        return (
          (g.blackUsername === this.selectedUsername ||
            g.whiteUsername === this.selectedUsername) &&
          g.blackResult !== TOURNAMENT_GAME_RESULT.UNFINISHED &&
          g.whiteResult !== TOURNAMENT_GAME_RESULT.UNFINISHED
        );
      });

      const playedAgainstUsernames = highlightUserPlayedGames.map((g) =>
        g.whiteUsername === this.selectedUsername
          ? g.blackUsername
          : g.whiteUsername
      );

      return playedAgainstUsernames.includes(target);
    },

    isPaired(username) {
      const roundPairedPlayerUsernames = this.roundGames
        .map((g) => [g.whiteUsername, g.blackUsername])
        .flat();
      return roundPairedPlayerUsernames.includes(username);
    },

    isAbsent(username) {
      const player = this.players.find((p) => p.username === username);
      return !isPlayerInRound(player, this.round.roundNumber);
    },

    getColorPref(username) {
      const player = this.players.find((p) => p.username === username);
      if (!player) {
        return "+0";
      }

      return getColorPref(this.round, player);
    },

    findPlayer(username) {
      return this.players.find((p) => p.username === username);
    },

    // Drag and Drop Methods
    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },

    handleDragEnter(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    handleDragLeave(event) {
      // Only remove highlight if we're actually leaving the container
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.isDragOver = false;
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;

      try {
        const dragData = JSON.parse(event.dataTransfer.getData("text/plain"));
        if (dragData.type === "game-player") {
          // Remove player from game by setting their position to null
          const game = this.roundGames.find(
            (g) => g.gameId === dragData.gameId
          );
          if (game) {
            if (dragData.color === "white") {
              this.$emit("set-players", game, null, game.blackUsername);
            } else {
              this.$emit("set-players", game, game.whiteUsername, null);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing drag data:", error);
      }
    },
  },
};
</script>

<style scoped>
.tournament-player-tile-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
}

/* Header styling matching TournamentRoundTable */
.tile-container-header {
  display: flex;
  align-items: center;
  background-color: #5d095d;
  color: white !important;
  height: 3rem;
  border-bottom: 2px solid #dbdbdb;
  padding: 0.5rem 1rem;
  flex-shrink: 0;
}

.header-text {
  color: white !important;
}

.header-info {
  color: #b3d9ff !important; /* Light blue for info text */
}

/* Drop zone styling - takes available height only */
.tile-container-content {
  transition: background-color 0.2s ease, border-color 0.2s ease;
  border: 2px dashed transparent;
  border-radius: 4px;
  position: relative;
  flex: 1;
  min-height: 0; /* Allow flex item to shrink below content size */
  display: flex;
  flex-direction: column;
}

.tiles-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0; /* Allow flex item to shrink below content size */
}

.tile-container-content.drop-zone-active {
  border: 2px dashed transparent;
  background-color: #e8f5e8;
  border-color: #4caf50;
}

.tile-container-content.drop-zone-active::before {
  content: "Drop here to remove player from game";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #4caf50;
  font-weight: bold;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 10;
}
</style>
